import clsx from 'clsx';
import {
  type ElementType,
  forwardRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { isElement } from 'react-is';

import { ChevronRightSmallIcon } from '@dynatrace/strato-icons';

import {
  breadcrumbsItemCSS,
  breadcrumbsListItemCSS,
  breadcrumbsSeperatorIconCSS,
} from './Breadcrumbs.sty.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import { DataTestId } from '../../core/types/data-props.js';
import { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { Link } from '../../typography/link/Link.js';

//#region Typings
/**
 * @public
 */
export interface BreadcrumbsItemOwnProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  /**
   * Whether the breadcrumb item is disabled.
   * If `true`, it prevents the user from interacting with the item and the item
   * being focused.
   */
  disabled?: boolean;
  /**
   * The url the breadcrumb links to.
   */
  href?: string;
}

/**
 * @public
 */
export type BreadcrumbsItemProps<E extends ElementType> =
  PolymorphicComponentProps<E, BreadcrumbsItemOwnProps>;
// #endregion

/**
 * Allows to check if a ReactNode is a BreadcrumbItem element.
 * @internal
 */
export function isBreadcrumbItem<E extends ElementType>(
  node: ReactNode,
): node is ReactElement<BreadcrumbsItemProps<E>> {
  return (
    node !== undefined && node !== null && isElement(node) && node.type === Item
  );
}

/**
 * The component that is used for the individual Breadcrumb items.
 * @public
 */
export const Item: <E extends ElementType = 'a'>(
  props: BreadcrumbsItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: BreadcrumbsItemProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      disabled = false,
      href,
      isLast,
      isLastVisible,
      isCollapsed,
      className: consumerClassName,
      style: consumerStyle,
      'data-id': dataId,
      'data-testid': dataTestId,
      as = 'a' as E,
      ...remainingProps
    }: BreadcrumbsItemProps<E> = props;

    return (
      <li
        className={clsx(
          breadcrumbsListItemCSS({ isLastVisible }),
          consumerClassName,
        )}
        style={consumerStyle}
        data-id={dataId}
        data-testid={dataTestId}
      >
        <Link
          className={clsx(
            focusRingStatic({
              minimal: true,
              color: remainingProps.menuTrigger ? 'neutral' : 'primary',
              disabled,
            }),
            breadcrumbsItemCSS({
              disabled,
              isLastItem: isLast,
            }),
          )}
          ref={forwardedRef}
          as={as satisfies ElementType}
          href={href}
          aria-disabled={disabled}
          aria-current={isLast && 'page'}
          {...remainingProps}
          tabIndex={!disabled ? 0 : -1}
        >
          {children}
        </Link>

        {!isCollapsed && !isLast && (
          <ChevronRightSmallIcon className={breadcrumbsSeperatorIconCSS} />
        )}
      </li>
    );
  },
);

(Item as typeof Item & { displayName: string }).displayName = 'Breadcrumb.Item';
